import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Card,
  CardHeader,
  CopyButton,
  PageHeader,
  Radio,
  RadioGroup,
  Select,
  TextInput,
  Toggle,
} from '@opendate-app/setlist';
import CurrencyInput from '../CurrencyInput/components/CurrencyInput';

const FACE = 'face';
const ALL_IN = 'all_in';

const num = (value) => parseFloat(value) || 0;
const cents = (value) => Math.round(value * 100) / 100;

const feeDescription = (flat, percentage, money) => {
  const parts = [];
  if (num(flat) > 0) parts.push(money(num(flat)));
  if (num(percentage) > 0) parts.push(`${num(percentage)}%`);
  return parts.length ? parts.join(' + ') : 'None';
};

const tierDescription = (rule, money) => {
  if (num(rule.minimum) <= 0.01 && num(rule.maximum) >= 999999) return 'All prices';
  if (num(rule.maximum) >= 999999) return `${money(num(rule.minimum))} and up`;
  return `${money(num(rule.minimum))} – ${money(num(rule.maximum))}`;
};

const mutedText = { color: 'var(--sl-text-tertiary)' };
const labelText = { fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)', marginBottom: 8 };

const Row = ({ label, value, strong, ruled }) => (
  <div
    className="d-flex justify-content-between"
    style={{
      padding: '8px 0',
      fontSize: strong ? 16 : 14,
      fontWeight: strong ? 600 : 400,
      color: strong ? 'var(--sl-text-primary)' : 'var(--sl-text-secondary)',
      borderTop: ruled ? '1px solid var(--sl-border-secondary)' : 'none',
    }}
  >
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

const TicketFeeCalculatorApp = ({ currency, selectedVenueId, venues }) => {
  const initialVenue = venues.find((v) => v.id === selectedVenueId) || venues[0];
  const [venueId, setVenueId] = useState(initialVenue?.id ?? null);
  const [mode, setMode] = useState(FACE);
  const [input, setInput] = useState('25.00');
  const [roundUp, setRoundUp] = useState(Boolean(initialVenue?.defaultRoundUp));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' });
  const money = (value) => formatter.format(value);
  const amount = num(String(input).replace(/[$,]/g, ''));
  const venue = venues.find((v) => v.id === venueId);

  useEffect(() => {
    if (!(amount > 0) || !venueId) { setResult(null); setError(null); return undefined; }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = { venue_ownership_id: venueId, [mode === FACE ? 'face_value' : 'target_all_in']: amount };
    const timer = setTimeout(() => {
      axios.get('/ticket-fee-quote', { params })
        .then(({ data }) => { if (!cancelled) setResult({ face: data.face_value, quote: data }); })
        .catch((e) => {
          if (cancelled) return;
          setResult(null);
          setError(e?.response?.data?.error || 'Could not calculate fees for this price. Check the price and try again.');
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 350);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [amount, mode, venueId]);

  const switchMode = (nextMode) => {
    if (nextMode === mode) return;
    if (result) {
      const shown = roundUp ? result.quote.rounded : result.quote.base;
      setInput((nextMode === FACE ? result.face : shown.all_in_total).toFixed(2));
    }
    setMode(nextMode);
  };

  const face = result?.face ?? 0;
  const quote = result ? (roundUp ? result.quote.rounded : result.quote.base) : null;
  const rule = venue?.feeRules.find((r) => face >= num(r.minimum) && face <= num(r.maximum));

  let heroLabel = null;
  let heroValue = null;
  let heroSub = null;
  let note = null;
  if (quote) {
    const base = result.quote.base;
    const rounded = result.quote.rounded;
    if (mode === FACE) {
      heroLabel = 'All-in price';
      heroValue = money(quote.all_in_total);
      heroSub = `What the fan pays at checkout on ${money(face)} face value`;
      if (roundUp) {
        note = rounded.round_up_amount > 0
          ? `${money(rounded.round_up_amount)} Round Up goes to the venue. Processing on the rounded total is already covered.`
          : 'Already a whole dollar, so there is nothing to round.';
      } else if (rounded.round_up_amount > 0) {
        note = `Turn on Round Up to land on ${money(rounded.all_in_total)} and send ${money(rounded.round_up_amount)} to the venue.`;
      }
    } else {
      const gap = cents(amount - quote.all_in_total);
      heroLabel = 'Face value to set';
      heroValue = money(face);
      heroSub = gap === 0
        ? `Lands on ${money(amount)} all-in`
        : `Lands at ${money(quote.all_in_total)}, ${money(Math.abs(gap))} ${gap > 0 ? 'under' : 'over'} ${money(amount)} all-in`;
      if (roundUp) {
        note = rounded.all_in_total === amount
          ? (rounded.round_up_amount > 0
            ? `Round Up closes the last ${money(rounded.round_up_amount)} so the fan pays exactly ${money(amount)}. That goes to the venue.`
            : `Lands exactly on ${money(amount)} with no rounding.`)
          : `Round Up always rounds to the next whole dollar, so the fan pays ${money(rounded.all_in_total)}.`;
      } else if (cents(amount - base.all_in_total) > 0 && Number.isInteger(amount)) {
        note = `Processing fees move with price, so this can't land exactly on ${money(amount)}. Turn on Round Up to close the gap.`;
      }
    }
  }

  const breakdownText = () => [
    venue ? `Venue: ${venue.name}` : null,
    `Face value: ${money(face)}`,
    ...Object.entries(quote.custom_charges).map(([name, value]) => `${name}: ${money(num(value))}`),
    `Fees: ${money(quote.opendate_fee + quote.organizer_fee)}`,
    quote.rebate_fee > 0 ? `Rebate fee: ${money(quote.rebate_fee)}` : null,
    `Processing fee: ${money(quote.processing_fee)}`,
    roundUp && quote.round_up_amount > 0 ? `Round Up: ${money(quote.round_up_amount)}` : null,
    `All-in total (before taxes): ${money(quote.all_in_total)}`,
  ].filter(Boolean).join('\n');

  return (
    <div className="container-fluid" style={{ maxWidth: 1040, paddingTop: 16, paddingBottom: 32 }}>
      <PageHeader
        title="Ticket Fee Calculator"
        supportingText="Answer “what does the service fee need to be?” without building a ticket."
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 24,
          marginTop: 24,
          alignItems: 'start',
        }}
      >
        <Card header={<CardHeader title="Ticket price" />}>
          <div className="d-flex flex-column" style={{ gap: 20 }}>
            {venues.length > 1 && (
              <Select
                label="Venue"
                options={venues.map((v) => ({ value: v.id, label: v.name }))}
                value={venueId}
                onChange={(id) => {
                  setVenueId(id);
                  setRoundUp(Boolean(venues.find((v) => v.id === id)?.defaultRoundUp));
                }}
              />
            )}

            <div>
              <div style={labelText}>Start from</div>
              <div className="d-flex flex-wrap" style={{ gap: '8px 24px' }}>
                <RadioGroup name="ticket-fee-calculator-mode" value={mode} onChange={switchMode}>
                  <Radio value={FACE} label="Face value" />
                  <Radio value={ALL_IN} label="Target all-in price" />
                </RadioGroup>
              </div>
            </div>

            <TextInput
              as={CurrencyInput}
              label={mode === FACE ? 'Face value' : 'Target all-in price'}
              hint={mode === FACE ? 'The ticket price before fees.' : 'What the agent wants the fan to pay.'}
              leadingText="$"
              maskOptions={{ prefix: '', integerLimit: null }}
              value={input}
              onChange={(e) => setInput(e.target.value.replace(/[$,]/g, ''))}
              onFocus={(e) => e.target.select()}
              placeholder="0.00"
            />

            <Toggle
              id="ticket-fee-calculator-round-up"
              label="Round Up: round the total up to the next dollar. The difference goes to the venue."
              checked={roundUp}
              onChange={(e) => setRoundUp(e.target.checked)}
            />

            {rule && (
              <div
                style={{
                  background: 'var(--sl-bg-secondary)',
                  border: '1px solid var(--sl-border-secondary)',
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 13,
                }}
              >
                <div style={{ ...labelText, fontSize: 13 }}>
                  {venue.venueFees ? 'Venue fee rules' : 'Team fee rules'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 16, rowGap: 4 }}>
                  <span style={mutedText}>Tier applied</span><span>{tierDescription(rule, money)}</span>
                  <span style={mutedText}>Organizer fee</span><span>{feeDescription(rule.organizer_service_fee, rule.organizer_service_fee_percentage, money)}</span>
                  <span style={mutedText}>Opendate fee</span><span>{feeDescription(rule.opendate_service_fee, rule.opendate_service_fee_percentage, money)}</span>
                  <span style={mutedText}>Processing</span><span>{feeDescription(rule.processing_fee_cash, rule.processing_fee_percentage, money)}</span>
                </div>
              </div>
            )}
          </div>
        </Card>

        <Card
          header={(
            <CardHeader
              title="Breakdown"
              actions={quote ? <CopyButton label="Copy" size="sm" variant="secondary" value={breakdownText} /> : null}
            />
          )}
        >
          {error && <Alert color="error" title={error} />}

          {!error && !quote && (
            <p style={{ ...mutedText, margin: 0 }}>
              {venues.length ? 'Enter a price to see the fees.' : "You don't have access to any venues to price for."}
            </p>
          )}

          {!error && quote && (
            <div style={{ opacity: loading ? 0.5 : 1 }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ ...mutedText, fontSize: 14 }}>{heroLabel}</div>
                <div style={{ fontSize: 32, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{heroValue}</div>
                <div style={{ ...mutedText, fontSize: 13 }}>{heroSub}</div>
              </div>

              <Row label="Base price" value={money(face)} />
              {Object.entries(quote.custom_charges).map(([name, value]) => (
                <Row key={name} label={name} value={money(num(value))} />
              ))}
              <Row label="Fees" value={money(quote.opendate_fee + quote.organizer_fee)} />
              {quote.rebate_fee > 0 && <Row label="Rebate fee" value={money(quote.rebate_fee)} />}
              <Row label="Processing fee" value={money(quote.processing_fee)} />
              {roundUp && quote.round_up_amount > 0 && <Row label="Round Up fee" value={money(quote.round_up_amount)} />}
              <Row ruled label="Subtotal" value={money(quote.all_in_total)} />
              <Row label="Taxes" value="Set per event" />
              <Row ruled strong label="Total" value={money(quote.all_in_total)} />

              {note && (
                <div style={{ marginTop: 16 }}>
                  <Alert color={roundUp ? 'brand' : 'gray'} title={note} />
                </div>
              )}

              <p style={{ ...mutedText, fontSize: 12, marginTop: 16, marginBottom: 0 }}>
                Same calculation as the ticket builder, card payments, per ticket. Forecasting aid only. Nothing is saved.
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

TicketFeeCalculatorApp.propTypes = {
  currency: PropTypes.string,
  selectedVenueId: PropTypes.number,
  venues: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    venueFees: PropTypes.bool,
    defaultRoundUp: PropTypes.bool,
    feeRules: PropTypes.arrayOf(PropTypes.object).isRequired,
  })).isRequired,
};

export default TicketFeeCalculatorApp;
